// iter138 — Article detail page for admin-managed Tips (categories: Tips,
// News, Customs, Alerts). Rendered at /tips/:id. The body content renders
// automatically on page load — no extra taps required.
//
// Data source: GET /api/tips returns the entire active deck. We pick the
// requested article by id from that list (cache-first, network second).
// `id` may also be a special key like "exchange" or "connectivity" so we
// also match against `category` and `slug` for back-compat with old links.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import { loadTips, readTipsCache, pickLang } from "../lib/tips";

// Category → human label + accent colour for the eyebrow.
function categoryMeta(category, lang) {
  const c = (category || "general").toLowerCase();
  const map = {
    tip:     { en: "TIP",     es: "CONSEJO" },
    tips:    { en: "TIP",     es: "CONSEJO" },
    news:    { en: "NEWS",    es: "NOTICIA" },
    customs: { en: "CUSTOMS", es: "ADUANA"  },
    alert:   { en: "ALERT",   es: "AVISO"   },
    alerts:  { en: "ALERT",   es: "AVISO"   },
    general: { en: "TIP",     es: "CONSEJO" },
  };
  const labels = map[c] || map.general;
  return labels[lang === "es" ? "es" : "en"];
}

export default function TipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [tips, setTips] = useState(() => readTipsCache() || []);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Always try to refresh from the API so the user sees the latest body
    // even if their cache is stale — but render the cached copy meanwhile.
    loadTips()
      .then((rows) => {
        if (cancelled) return;
        setTips(rows || []);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  const tip = useMemo(() => {
    if (!tips || tips.length === 0) return null;
    // Primary: match by id.
    let row = tips.find((x) => x.id === id);
    // Fallback: legacy callers used semantic ids ("exchange", "connectivity")
    // — match against category.
    if (!row && id) {
      row = tips.find((x) => (x.category || "").toLowerCase() === id.toLowerCase());
    }
    return row || null;
  }, [tips, id]);

  // Loading state — initial cache miss
  if (!tip && !loaded) {
    return (
      <main data-testid="tip-detail-loading" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
        <Header navigate={navigate} />
        <div className="px-8 mt-16 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/40" />
        </div>
        <BottomNav />
      </main>
    );
  }

  // Not found — show clear empty state + fallback link back to the deck.
  if (!tip) {
    return (
      <main data-testid="tip-detail-notfound" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
        <Header navigate={navigate} />
        <div className="px-6 mt-16 text-center">
          <p className="mb-6"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#B0AAA3", lineHeight: 1.6 }}>
            {lang === "es"
              ? "Este artículo ya no está disponible."
              : "This article is no longer available."}
          </p>
          <Link
            to="/tips"
            data-testid="tip-detail-back-to-list"
            className="inline-flex items-center justify-center"
            style={{
              padding: "12px 24px",
              background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
              color: "#0B0B0B",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              minHeight: 48,
            }}
          >
            {lang === "es" ? "Ver todos los consejos" : "View all tips"}
          </Link>
        </div>
        <BottomNav />
      </main>
    );
  }

  const title = pickLang(tip.title, lang) || "";
  const body = pickLang(tip.body, lang) || "";
  const urgentLabel = pickLang(tip.urgent_label, lang) || "";
  const eyebrow = categoryMeta(tip.category, lang);

  // Split the body on double-newlines to render paragraphs. Single newlines
  // preserved within paragraphs (e.g. lists).
  const paragraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <main data-testid="tip-detail-page" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      <Header navigate={navigate} />

      <article className="px-6 mt-6" data-testid="tip-detail-article">
        {/* Category eyebrow */}
        <div
          className="mb-3"
          data-testid="tip-detail-category"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            color: tip.urgent ? "#F5A623" : "#E8B84B",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {eyebrow}
        </div>

        {/* Headline */}
        <h1
          data-testid="tip-detail-title"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 28,
            lineHeight: 1.18,
            color: "#F5F1E8",
            margin: 0,
          }}
        >
          {title}
        </h1>

        {/* Urgent banner */}
        {tip.urgent && urgentLabel && (
          <div
            data-testid="tip-detail-urgent"
            className="mt-4 flex items-center gap-2"
            style={{
              background: "rgba(245,158,11,0.08)",
              border: "1px solid rgba(245,158,11,0.35)",
              color: "#F5A623",
              padding: "10px 14px",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              letterSpacing: "0.06em",
            }}
          >
            <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.5} />
            <span>{urgentLabel}</span>
          </div>
        )}

        {/* Body — auto-rendered, paragraph-aware. */}
        <div
          className="mt-6 space-y-4"
          data-testid="tip-detail-body"
        >
          {paragraphs.length > 0 ? (
            paragraphs.map((p, i) => (
              <p
                key={i}
                data-testid={`tip-detail-paragraph-${i}`}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 16,
                  lineHeight: 1.65,
                  color: "#E8E2D5",
                  whiteSpace: "pre-wrap",
                  margin: 0,
                }}
              >
                {p}
              </p>
            ))
          ) : (
            <p style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#B0AAA3", lineHeight: 1.6,
            }}>
              {lang === "es"
                ? "Este artículo aún no tiene contenido."
                : "This article doesn’t have body content yet."}
            </p>
          )}
        </div>

        {/* Footer: back-to-list affordance */}
        <div className="mt-10">
          <Link
            to="/tips"
            data-testid="tip-detail-back-to-deck"
            className="inline-flex items-center justify-center gap-2 w-full"
            style={{
              minHeight: 48,
              background: "transparent",
              border: "1px solid rgba(232,184,75,0.5)",
              color: "#E8B84B",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: "0.04em",
            }}
          >
            {lang === "es" ? "Ver más consejos" : "View more tips"}
          </Link>
        </div>
      </article>

      <BottomNav />
    </main>
  );
}

function Header({ navigate }) {
  return (
    <header className="px-5 pt-6 flex items-center gap-3">
      <button
        type="button"
        onClick={() => navigate(-1)}
        data-testid="tip-detail-back"
        aria-label="Back"
        className="grid place-items-center"
        style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}
      >
        <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
      </button>
    </header>
  );
}
